import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { DashboardSmartView, SmartViewInlineActions } from '../types';
import { FontSizes } from '../../../types/common/types';
import { WebComponentEvent } from '../../../framework/BaseWebComponentController';
import _, { isEqual } from 'lodash';
import { FormValidator } from '../../../ui/gzForm/formField/types/form-validator';
import { connect } from 'pwa-helpers';
import { RootState, store } from '../../../redux/store';
import { repeat } from 'lit/directives/repeat.js';
import {
  changeActiveSmartView,
  deleteSmartView,
  renameSmartView,
  selectActiveViewId,
  selectAllSmartViewsCategories,
  selectDashboardHasUnsavedChanges,
  selectDefaultView,
  selectFilteredViewsForDashboard,
  selectSearchTextFromSmartViews,
  setShouldDisplayUnsavedChangesPopUp,
  toggleIsOpenStateForSmartViewsCategory,
  updateSmartViewsSearchText,
} from '../../redux/dashboards.smart.views.slice';
import { SelectOption } from '../../../ui/gzSelect/types';
import { translateFn } from '../../../utils/translator.helper';
import { GzListItemWebComponent } from '../../../ui/gzListItem';
import { GzAccordionWebComponent } from '../../../ui/gzAccordion';
import { DashboardAppState, selectDashboardsAppState } from '../../redux/dashboards.app.reducer';
import { debounce } from '../../../utils/event.utils';
import { CategoryGroup, SmartViewCategoryConfig } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { DashboardPopUpFactory, DashboardPopUpType } from '../../utils/dashboard.pop-up.factory';
import { selectShouldDisplayUnsavedChangesPopUp } from '../../redux/dashboards.ui.slice';
import { emphasizeString } from '../../../utils/htmlElement.utils';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { htmlEncode } from '../../../utils/escape.utils';

/**
 * @summary Container that includes a configurable search input and a list of categories and views
 * @tag gz-dashboard-smart-views-list
 */
@customElement('gz-dashboard-smart-views-list')
export class GzDashboardSmartViewsListWebComponent extends connect(store)(LitElement) {
  /**
   * Configures the presence of search input
   *
   * @type {boolean}
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  public hasSearchInput = false;
  /**
   * Placeholder for search input
   *
   * @type {string}
   */
  @property({ type: String, attribute: true, reflect: true })
  public searchInputPlaceholder = translateFn('Search views');
  /**
   * No results found text when search returned no results
   *
   * @type {string}
   */
  @property({ type: String, attribute: true, reflect: true })
  public searchInputNoResultsFoundText = translateFn('No results found');
  /**
   * Array of validators for view name
   * path: apps/webcomponents/src/ui/gzForm/validators
   *
   * @type {Array<FormValidator>}
   */
  @property({
    type: Array,
    reflect: false,
  })
  public smartViewValidators?: Array<FormValidator>;
  /**
   * Rename input max length
   *
   * @type {number}
   */
  @property({
    type: Number,
    reflect: false,
  })
  public smartViewMaxInputLength? = 64;

  /**
   * Empty search text when the input is present
   *
   * @type {string}
   * @private
   */
  @state()
  private searchModeEnabled = false;

  @state()
  private _searchText = '';

  @state({
    hasChanged(value: Array<SmartViewCategoryConfig>, oldValue: Array<SmartViewCategoryConfig>): boolean {
      return !isEqual(value, oldValue);
    },
  })
  private categories: Array<SmartViewCategoryConfig>;

  @state({
    hasChanged(value: Array<DashboardSmartView>, oldValue: Array<DashboardSmartView>): boolean {
      return !isEqual(value, oldValue);
    },
  })
  private views: Array<DashboardSmartView>;

  private hasUnsavedChanges: boolean;

  private shouldDisplayUnsavedChangesPopUp: boolean;

  private activeViewId: string;

  private defaultView: DashboardSmartView;

  static get styles() {
    return styles;
  }

  stateChanged(rootState: RootState) {
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    this.searchModeEnabled = !!selectSearchTextFromSmartViews(dashboardsAppState);
    this.views = selectFilteredViewsForDashboard(dashboardsAppState);
    this.categories = selectAllSmartViewsCategories(dashboardsAppState);
    this.hasUnsavedChanges = selectDashboardHasUnsavedChanges(dashboardsAppState);
    this.shouldDisplayUnsavedChangesPopUp = selectShouldDisplayUnsavedChangesPopUp(dashboardsAppState);
    this.activeViewId = selectActiveViewId(dashboardsAppState);
    this.defaultView = selectDefaultView(dashboardsAppState);
  }

  protected render() {
    return html`
      <div class="content">
        ${this.renderSearchInput()}
        <div class="categories">${this.renderCategories()}</div>
        ${this.renderNoItemsFound()}
      </div>
    `;
  }

  private renderSearchInput() {
    return this.hasSearchInput
      ? html` <div class="search-input">
          <gz-input-search
            id="views-input-search"
            placeholder="${this.searchInputPlaceholder}"
            @change="${debounce(this.handleSearchInputChange, 100)}"
          ></gz-input-search>
        </div>`
      : nothing;
  }

  private renderCategories() {
    return html`${repeat(this.categories, (category: SmartViewCategoryConfig) => this.renderCategory(category))}`;
  }

  private renderCategory(category: SmartViewCategoryConfig) {
    const viewsFromCategory = this.views.filter(
      (dashboardSmartView: DashboardSmartView) => dashboardSmartView.categoryGroup === category.group
    );

    return this.searchModeEnabled && !viewsFromCategory.length
      ? nothing
      : this.renderCategoryByCollapsableFlag(category, viewsFromCategory);
  }

  private renderCategoryByCollapsableFlag(
    category: SmartViewCategoryConfig,
    viewsFromCategory: Array<DashboardSmartView>
  ) {
    return html` <gz-accordion
      ?isOpen="${this.searchModeEnabled ? true : category.isOpen ?? true}"
      ?nonCollapsable="${this.searchModeEnabled ? true : !category.collapsable}"
      isCompact="true"
      align="left"
      @open="${this.handleOpenCategoryAction}"
      @close="${this.handleCloseCategoryAction}"
      data-category="${category.id}"
    >
      <span slot="dynamic-title-content" class="accordion-slot d-inline-flex">
        <gz-tooltip class="accordion-title" tooltiptext="${category.label}" isellipsis="true"
          >${category.label}</gz-tooltip
        >
        <gz-text class="accordion-count" fontWeight="300" fontSize="10"
          >${viewsFromCategory.length ? `(${viewsFromCategory.length})` : nothing}</gz-text
        >
      </span>
      ${this.renderViewsList(category, viewsFromCategory)}
    </gz-accordion>`;
  }

  private renderViewsList(category: SmartViewCategoryConfig, categorySmartViews: Array<DashboardSmartView>) {
    if (!categorySmartViews.length) {
      return html` <gz-list-item
        class="align-items-center"
        emptyState="true"
        isCompact="true"
        fontSize="${FontSizes.PARAGRAPH_12}"
      >
        ${category.emptyCategoryText}
      </gz-list-item>`;
    }

    const inlineActions = this.getInlineActionsForCategory(category);

    return html`${repeat(
      categorySmartViews,
      (smartView: DashboardSmartView) => smartView.label,
      (smartView: DashboardSmartView) => this.renderView(smartView, inlineActions)
    )}`;
  }

  private renderView(view: DashboardSmartView, inlineActions: Array<SelectOption>) {
    const label = htmlEncode(view.label);
    const decoratedLabel = this.searchModeEnabled ? view.decoratedTextOnSearch : label;

    if (this.searchModeEnabled) {
      this.filterViews(this._searchText);
    }

    return html`<gz-list-item
      class="align-items-center"
      .inlineActions="${inlineActions}"
      .itemData="${view}"
      .errorMessage="${this.smartViewValidators}"
      .inputMaxLength="${this.smartViewMaxInputLength}"
      ?active="${view.active}"
      @rename="${this.handleViewRename}"
      @save="${this.handleViewRenameSave}"
      @delete="${this.handleViewDelete}"
      @itemClick="${this.handleViewItemClick}"
      isCompact="true"
      >${!decoratedLabel ? label : nothing}${unsafeHTML(decoratedLabel ? decoratedLabel : '')}</gz-list-item
    >`;
  }

  private renderNoItemsFound() {
    return this.views.length ? nothing : html` <div class="no-items mt-2">${this.searchInputNoResultsFoundText}</div>`;
  }

  private handleSearchInputChange(event: WebComponentEvent) {
    const searchInput = event.detail.sourceEvent.trim().toLowerCase();
    this._searchText = searchInput;
    store.dispatch(updateSmartViewsSearchText(searchInput));
    this.filterViews(searchInput);
  }

  private handleViewRename(event: WebComponentEvent) {
    const listItem = event.currentTarget as GzListItemWebComponent;

    if (!listItem) {
      throw new Error('Unknown view rename event target');
    }

    listItem.editable = true;
  }

  private handleViewRenameSave(event: WebComponentEvent) {
    const updatedView: DashboardSmartView = event.detail.itemData;
    const newName: string = event.detail.value.trimStart().trimEnd();

    store.dispatch(
      renameSmartView({
        smartView: updatedView,
        newName: newName,
      })
    );
  }

  private handleViewDelete(event: WebComponentEvent): void {
    DashboardPopUpFactory.createPopUp(
      DashboardPopUpType.DELETE_VIEW_DIALOG,
      async () => {
        if (this.activeViewId === event.detail.itemData.id) {
          await store.dispatch(changeActiveSmartView(this.defaultView.id));
        }
        await store.dispatch(deleteSmartView(event.detail.itemData.id));
      },
      () => {},
      {
        viewName: event.detail.itemData.label,
      }
    );
  }

  private handleOpenCategoryAction(event: WebComponentEvent) {
    this.handleOpenClose(event, true);
  }

  private handleCloseCategoryAction(event: WebComponentEvent) {
    this.handleOpenClose(event, false);
  }

  private handleOpenClose(event: WebComponentEvent, isOpen: boolean) {
    const evCategory = event.currentTarget as GzAccordionWebComponent;
    if (!evCategory) {
      throw new Error('Undefined target for category openClose event');
    }

    const categoryId = evCategory.getAttribute('data-category');

    const eventCategory = this.categories.find(category => category.id === categoryId);

    if (!eventCategory) {
      throw new Error('UNKNOWN CATEGORY');
    }

    store.dispatch(
      toggleIsOpenStateForSmartViewsCategory({
        category: eventCategory,
        isOpen: isOpen,
      })
    );
  }

  private handleViewItemClick(event: WebComponentEvent) {
    const selectedView = event.detail.itemData as DashboardSmartView;

    if (!this.hasUnsavedChanges || !this.shouldDisplayUnsavedChangesPopUp) {
      this.changeActiveView(selectedView);
      return;
    }

    DashboardPopUpFactory.createPopUp(DashboardPopUpType.UNSAVED_CHANGES_DIALOG, (ev: WebComponentEvent) => {
      store.dispatch(setShouldDisplayUnsavedChangesPopUp(ev.detail.showAgain));
      this.changeActiveView(selectedView);
    });
  }

  private changeActiveView(selectedView: DashboardSmartView) {
    this.setAttribute('disabled', 'true');
    const selectedCategory = this.categories.find(
      (category: SmartViewCategoryConfig) => category.group === selectedView.categoryGroup
    );

    if (!selectedCategory) {
      throw new Error('Selected view without category!');
    }

    store.dispatch(changeActiveSmartView(selectedView.id));

    if (!selectedCategory.isOpen) {
      store.dispatch(
        toggleIsOpenStateForSmartViewsCategory({
          category: selectedCategory,
          isOpen: true,
        })
      );
    }
  }

  private getInlineActionsForCategory(category: SmartViewCategoryConfig): Array<SelectOption> {
    switch (category.group) {
      case CategoryGroup.DEFAULT_SECOND:
      case CategoryGroup.DEFAULT_TOP:
      default:
        return [];
      case CategoryGroup.SAVED:
        return [
          {
            title: translateFn('Rename'),
            value: SmartViewInlineActions.RENAME,
          },
          {
            title: translateFn('Delete'),
            value: SmartViewInlineActions.DELETE,
            customClass: 'danger',
          },
        ];
    }
  }

  private filterViewsAfterAction(): void {
    if (typeof this._searchText !== 'undefined') {
      this.filterViews(this._searchText);
    }
  }

  filterViews(filter: string): void {
    const filteredViews = _.cloneDeep(this.views);

    filteredViews.forEach(view => {
      view.decoratedTextOnSearch = emphasizeString(filter, htmlEncode(view.label));
    });

    this.views = filteredViews;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-dashboard-smart-views-list': GzDashboardSmartViewsListWebComponent;
  }
}
